import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../utils/api';

const statCards = [
  {
    key: 'totalCalls',
    label: 'Total Calls',
    sub: '+12% vs last week',
    subColor: 'text-green-400',
    icon: (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    ),
  },
  {
    key: 'activeCalls',
    label: 'Active Calls',
    sub: 'Live real-time',
    subColor: '',
    icon: (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
  },
  {
    key: 'successRate',
    label: 'Success Rate',
    format: (v) => `${v}%`,
    sub: '+2.1% vs last week',
    subColor: 'text-green-400',
    icon: (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
  {
    key: 'avgDuration',
    label: 'Avg Duration',
    format: (v) => `${v}m`,
    sub: '+0.3m vs last week',
    subColor: 'text-green-400',
    icon: (
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
  },
];

const Dashboard = () => {
  const { userData } = useAuth();
  const [stats, setStats] = useState({ totalCalls: 0, activeCalls: 0, successRate: 0, avgDuration: 0, todayCalls: 0 });
  const [recentCalls, setRecentCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [buildMsg, setBuildMsg] = useState('');
  const [stage, setStage] = useState('');

  const assistantId = userData?.agent?.vapiAssistantId;
  const effectiveStage = stage || (assistantId ? 'demo_ready' : '');
  const showAeCta = assistantId && effectiveStage !== 'build_requested';

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const me = await api('/api/onboarding/me');
        if (!cancelled) setStage(me.onboardingStage || '');
      } catch (e) { console.error(e); }

      if (!assistantId) { if (!cancelled) setLoading(false); return; }

      try {
        const data = await api('/api/onboarding/calls');
        const calls = data.calls || [];
        const totalCalls = calls.length;
        const successfulCalls = calls.filter(c => c.status === 'ended' && c.endedReason === 'assistant-ended-conversation').length;
        const successRate = totalCalls ? Math.round((successfulCalls / totalCalls) * 100) : 0;
        const avgDuration = totalCalls ? Math.round(calls.reduce((s, c) => s + (c.duration || 0), 0) / totalCalls / 60) : 0;
        const today = new Date().toISOString().split('T')[0];
        const todayCalls = calls.filter(c => c.createdAt && String(c.createdAt).startsWith(today)).length;
        if (!cancelled) {
          setStats({ totalCalls, activeCalls: calls.filter(c => c.status === 'in-progress').length, successRate, avgDuration, todayCalls });
          setRecentCalls(calls.map(call => ({
            id: call.id,
            phoneNumber: call.customer?.number || call.customerPhoneNumber || 'Web Call',
            duration: call.duration || 0,
            status: call.status,
            createdAt: call.createdAt,
            endedReason: call.endedReason,
            cost: call.cost || 0,
          })));
        }
      } catch (e) { console.error(e); }
      finally { if (!cancelled) setLoading(false); }
    };
    load();
    return () => { cancelled = true; };
  }, [userData, assistantId]);

  const submitBuild = async () => {
    try {
      const r = await api('/api/onboarding/build-request', { method: 'POST', body: JSON.stringify({ notes: '' }) });
      setBuildMsg(r.message || 'Submitted.');
      setStage('build_requested');
    } catch (e) { setBuildMsg(e.message || 'Failed'); }
  };

  const getStatusText = (status, endedReason) => {
    if (status === 'in-progress') return 'In Progress';
    if (status === 'ended' && endedReason === 'assistant-ended-conversation') return 'Completed';
    if (status === 'ended' && endedReason === 'customer-ended-call') return 'Customer Ended';
    if (status === 'ended' && endedReason === 'assistant-ended-call') return 'Assistant Ended';
    if (status === 'ended' && endedReason === 'system-ended-call') return 'System Ended';
    return 'Unknown';
  };

  const getStatusColor = (status, endedReason) => {
    if (status === 'in-progress') return { color: '#818cf8' };
    if (status === 'ended' && endedReason === 'assistant-ended-conversation') return { color: '#4ade80' };
    if (status === 'ended' && endedReason === 'customer-ended-call') return { color: '#facc15' };
    return { color: '#f87171' };
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0s';
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return m > 0 ? `${m}m ${s}s` : `${s}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Unknown';
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const cardStyle = { backgroundColor: 'rgba(255,255,255,0.04)', border: '0.5px solid rgba(255,255,255,0.08)' };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-purple-500/40 border-t-purple-400 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>Loading dashboard…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-white p-4 md:p-6" style={{ backgroundColor: '#07070f' }}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight mb-1">
            {userData?.agent?.agentName || 'AI Assistant'} Dashboard
          </h1>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
            Your demo runs in the browser. Production — real number, routing, and automations — is set up with our team after you request a call.
          </p>
        </div>

        {/* Demo vs production banner */}
        {assistantId && (
          <div className="mb-8 rounded-2xl p-6 md:p-8" style={cardStyle}>
            <h2 className="text-base font-semibold text-white mb-3">Demo vs Production</h2>
            <ul className="text-sm space-y-2 mb-6" style={{ color: 'rgba(255,255,255,0.6)' }}>
              <li>
                <span className="text-white font-medium">Now (demo):</span> test your assistant with your mic on the{' '}
                <Link to="/app" className="text-purple-400 hover:text-purple-300 underline transition">Voice Agent</Link>{' '}
                page — web calls only.
              </li>
              <li>
                <span className="text-white font-medium">Production:</span> dedicated phone number, forwarding and routing rules, and integrations (CRM, calendar, etc.) configured for your business.
              </li>
              <li>
                <span className="text-white font-medium">Next step:</span> talk to an account executive to scope your setup and pricing — no payment required in the app.
              </li>
            </ul>

            {effectiveStage === 'build_requested' ? (
              <p className="text-green-400 text-sm">Request received. An account executive will contact you shortly.</p>
            ) : showAeCta ? (
              <div>
                <button
                  type="button"
                  onClick={submitBuild}
                  className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition hover:opacity-90"
                  style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}
                >
                  Have an account executive contact me
                </button>
                <p className="text-xs mt-2 max-w-xl" style={{ color: 'rgba(255,255,255,0.4)' }}>
                  We'll reach out to align on your phone setup, call flows, and any automations you need.
                </p>
              </div>
            ) : null}

            {buildMsg && <p className="text-purple-300 text-sm mt-3">{buildMsg}</p>}
          </div>
        )}

        {/* Agent meta */}
        {userData?.agent && (
          <div className="mb-8 text-xs flex flex-wrap gap-x-4 gap-y-1" style={{ color: 'rgba(255,255,255,0.4)' }}>
            <span>Voice: {userData.agent.agentVoice}</span>
            <span>
              Phone:{' '}
              {userData.agent.assignedPhoneNumber || <span style={{ color: 'rgba(255,255,255,0.25)' }}>Not assigned — demo is web-only</span>}
            </span>
            <span>Assistant ID: {userData.agent.vapiAssistantId?.slice(0, 8)}…</span>
          </div>
        )}

        {/* Stat cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {statCards.map((card) => {
            const value = stats[card.key];
            const display = card.format ? card.format(value) : value;
            return (
              <div key={card.key} className="rounded-2xl p-5" style={cardStyle}>
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-xs mb-1" style={{ color: 'rgba(255,255,255,0.45)' }}>{card.label}</p>
                    <p className="text-2xl font-bold text-white">{display}</p>
                    {card.sub && (
                      <p className={`text-xs mt-1 ${card.subColor}`} style={!card.subColor ? { color: 'rgba(255,255,255,0.3)' } : {}}>
                        {card.sub}
                      </p>
                    )}
                  </div>
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}>
                    {card.icon}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Recent calls */}
        <div className="rounded-2xl" style={cardStyle}>
          <div className="px-6 py-5" style={{ borderBottom: '0.5px solid rgba(255,255,255,0.06)' }}>
            <h2 className="text-base font-semibold text-white">Recent Calls</h2>
            <p className="text-xs mt-0.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Live call activity and history</p>
          </div>

          <div className="p-6">
            {recentCalls.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}>
                  <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                </div>
                <h3 className="text-base font-semibold text-white mb-2">No calls yet</h3>
                <p className="text-sm mb-5" style={{ color: 'rgba(255,255,255,0.45)' }}>
                  Test your assistant on the Voice Agent page — web demo calls will show here.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Link
                    to="/app"
                    className="px-5 py-2.5 rounded-xl text-sm font-medium transition hover:opacity-80"
                    style={{ backgroundColor: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }}
                  >
                    Open Voice Agent
                  </Link>
                  {showAeCta && (
                    <button
                      type="button"
                      onClick={submitBuild}
                      className="px-5 py-2.5 rounded-xl text-sm font-semibold text-white transition hover:opacity-90"
                      style={{ background: 'linear-gradient(135deg, #8B5CF6, #C084FC)' }}
                    >
                      Request account executive call
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {recentCalls.map((call) => (
                  <div
                    key={call.id}
                    className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 rounded-xl"
                    style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '0.5px solid rgba(255,255,255,0.06)' }}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-2 h-2 rounded-full bg-purple-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-white text-sm font-medium truncate">{call.phoneNumber}</p>
                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>{formatDate(call.createdAt)}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between sm:justify-end gap-6">
                      <div className="text-right">
                        <p className="text-sm font-medium text-white">{formatDuration(call.duration)}</p>
                        <p className="text-xs" style={getStatusColor(call.status, call.endedReason)}>{getStatusText(call.status, call.endedReason)}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-white">${(call.cost || 0).toFixed(2)}</p>
                        <p className="text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Cost</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
